import { Ajuda } from '../../design/Ajuda';
import { useCallback, useEffect, useState } from 'react';
import { ErroDeArea } from '../../design/Estados';
import { diaCurto, lerPainelDoGiro } from '../../giro';
import type { PainelDoGiro as Dados } from '../../giro';
import { SubnavDoGiro } from './SubnavDoGiro';

/**
 * PAINEL DO GIRO (dono, 26/09) — a visão do gestor sobre a reposição inteligente:
 *   - por setor: quantos produtos cadastrados, quantos com estoque e quantos já passaram pelo Giro
 *     (a cobertura — com o tempo, a loja inteira);
 *   - quem é mais rápido e mais lento (minutos por produto conferido, sem as pausas);
 *   - os produtos que mais giram (média de venda de 30 dias) e quantas vezes entraram no Giro.
 */

const PERIODOS = [7, 30, 90] as const;
const pct = (a: number, b: number) => (b > 0 ? Math.round((a / b) * 100) : 0);
const num = (n: number) => n.toLocaleString('pt-BR');

export function PainelDoGiro() {
  const [dias, setDias] = useState<(typeof PERIODOS)[number]>(30);
  const [d, setD] = useState<Dados | null>(null);
  const [erro, setErro] = useState(false);
  const carregar = useCallback(() => {
    setErro(false);
    setD(null);
    void lerPainelDoGiro(dias).then((r) => (r.ok ? setD(r.dados) : setErro(true)));
  }, [dias]);
  useEffect(carregar, [carregar]);

  const comMissao = d?.balconistas.filter((b) => b.minPorProduto !== null) ?? [];
  return (
    <section className="cv gr">
      <SubnavDoGiro atual="giro-painel" />
      <div className="cv-corpo">
        <div className="cv-topo">
          <div className="cv-titulo cv-titulo-2l">
            <h1>Painel do Giro</h1>
            <span>
              {d
                ? `${diaCurto(d.periodo.de)} a ${diaCurto(d.periodo.ate)} · a reposição inteligente da loja`
                : 'Carregando…'}
            </span>
          </div>
          <div className="gr-chips" role="tablist" aria-label="Período">
            {PERIODOS.map((p) => (
              <button
                key={p}
                type="button"
                role="tab"
                aria-selected={dias === p}
                className={dias === p ? 'gr-chip gr-chip-sel' : 'gr-chip'}
                onClick={() => setDias(p)}
              >
                {p} dias
              </button>
            ))}
          </div>
        </div>
        {erro ? (
          <ErroDeArea oQue="o painel do Giro" aoTentar={carregar} />
        ) : !d ? (
          <div className="cv-carregando" aria-busy="true" />
        ) : (
          <>
            <div className="gp-setores">
              {d.setores.map((s) => (
                <div key={s.id} className="cv-cartao gp-setor">
                  <div className="gp-setor-nome">
                    {s.nome}
                    <Ajuda rotulo={`a cobertura de ${s.nome}`}>
                      Dos produtos do setor que têm estoque, quantos já foram contados pelo menos
                      uma vez no Giro. 100% = todo o setor já passou pela conferência.
                    </Ajuda>
                  </div>
                  <div className="gp-setor-num">
                    <b>{num(s.jaPassaram)}</b>
                    <span>de {num(s.comEstoque)} com estoque já passaram pelo Giro</span>
                  </div>
                  <div
                    className="gp-barra"
                    role="progressbar"
                    aria-valuenow={pct(s.jaPassaram, s.comEstoque)}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  >
                    <i style={{ width: `${String(pct(s.jaPassaram, s.comEstoque))}%` }} />
                  </div>
                  <div className="gp-setor-rodape">
                    <span>{pct(s.jaPassaram, s.comEstoque)}% de cobertura</span>
                    <span>{num(s.cadastrados)} cadastrados</span>
                    <span>{num(s.noPeriodo)} no período</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="gp-duas">
              <div className="cv-cartao cv-sem-pad">
                <div className="cv-cartao-topo cv-pad">
                  <h2>
                    Balconistas · do mais rápido ao mais lento
                    <Ajuda rotulo="o ranking de tempo">
                      Quanto tempo cada um leva por produto no Giro (contar e repor), só com blocos
                      terminados no período. Divergências = casos em que o auditor achou diferente.
                    </Ajuda>
                  </h2>
                </div>
                {comMissao.length === 0 ? (
                  <p className="cv-vazio">Nenhum bloco concluído no período.</p>
                ) : (
                  <table className="cv-tabela">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Balconista</th>
                        <th className="cv-dir">
                          Min/produto
                          <Ajuda rotulo="minutos por produto">
                            Tempo ativo dividido pelo número de produtos. Quanto menor, mais rápido.
                          </Ajuda>
                        </th>
                        <th className="cv-dir">Blocos</th>
                        <th className="cv-dir">Produtos</th>
                        <th className="cv-dir">
                          Tempo ativo
                          <Ajuda rotulo="tempo ativo">
                            Do início ao fim dos blocos, tirando as pausas que a pessoa registrou.
                          </Ajuda>
                        </th>
                        <th className="cv-dir">Divergências</th>
                      </tr>
                    </thead>
                    <tbody>
                      {comMissao.map((b, i) => (
                        <tr key={b.accountId}>
                          <td className="cv-cinza">{i + 1}º</td>
                          <td className="cv-forte">
                            {b.nome}
                            {i === 0 && comMissao.length > 1 && (
                              <span className="gr-st gr-st-ok"> mais rápido</span>
                            )}
                            {i === comMissao.length - 1 && comMissao.length > 1 && (
                              <span className="gr-st gr-st-rep"> mais lento</span>
                            )}
                          </td>
                          <td className="cv-dir cv-forte">
                            {b.minPorProduto?.toLocaleString('pt-BR')}
                          </td>
                          <td className="cv-dir">
                            {b.concluidas}/{b.missoes}
                          </td>
                          <td className="cv-dir">{num(b.produtos)}</td>
                          <td className="cv-dir">{num(b.ativoMin)} min</td>
                          <td className="cv-dir">{b.divergencias}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="cv-cartao cv-sem-pad">
                <div className="cv-cartao-topo cv-pad">
                  <h2>
                    Produtos que mais giram
                    <Ajuda rotulo="os produtos que mais giram">
                      Os produtos que mais vendem por dia, em média, no período. &quot;No Giro&quot;
                      = quantas vezes ele entrou no Giro.
                    </Ajuda>
                  </h2>
                  <span className="cv-cinza cv-peq">média de venda por dia (30 dias)</span>
                </div>
                {d.maisGiram.length === 0 ? (
                  <p className="cv-vazio">Nenhum produto no Giro no período.</p>
                ) : (
                  <table className="cv-tabela">
                    <thead>
                      <tr>
                        <th>Produto</th>
                        <th>Setor</th>
                        <th className="cv-dir">Média/dia</th>
                        <th className="cv-dir">No Giro</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.maisGiram.map((p) => (
                        <tr key={p.productId}>
                          <td className="cv-forte">
                            {p.nome}
                            {p.mediaDiaria >= 8 && (
                              <span className="gr-st gr-st-ok"> alto giro</span>
                            )}
                          </td>
                          <td className="cv-cinza">{p.setor ?? '—'}</td>
                          <td className="cv-dir cv-forte">
                            {p.mediaDiaria.toLocaleString('pt-BR')}
                          </td>
                          <td className="cv-dir">{p.vezesNoGiro}×</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
